import { useFilterMyFleet, useGetFilter, useSearchValue, useSortOrder } from '../store';

import { CarEBoxLocation } from 'entities/cars';

import { getAutoStatusByDate } from 'shared/utils/getAutoStatusByDate';
import { MyFleetItems, MyFleetListItem } from '../types';
import { UserLocation } from 'entities/users';
import { useCarEBoxLocations } from 'entities/cars';
import { useUserCurrentLocation } from 'store/features/user/selectors/userLocation';
import { EMPTY_ARRAY } from 'common/constants';
import { useMemo } from 'react';

const useApplyFilters = (myFleetItems: MyFleetItems) => {
  const myFleetFilter = useFilterMyFleet();

  return useMemo(() => {
    let filterData = [...myFleetItems];

    if (myFleetFilter.locationStatus) {
      const filterParams = Object.keys(myFleetFilter.locationStatus).map((x) => Number(x));

      if (filterParams.length === 0) {
        return myFleetItems;
      }

      filterData = filterData?.filter(({ lastActivityDate }) => {
        const locationStatus = getAutoStatusByDate(lastActivityDate);
        return filterParams.includes(locationStatus);
      });
    }

    return filterData;
  }, [myFleetFilter.locationStatus, myFleetItems]);
};

const useApplySearchValue = (myFleetItems: MyFleetItems) => {
  const searchValue = useSearchValue();
  return useMemo(() => {
    if (searchValue.length === 0) {
      return myFleetItems;
    }

    return myFleetItems.filter(({ autoNumber }) => autoNumber?.toLowerCase().includes(searchValue.toLocaleLowerCase()));
  }, [myFleetItems, searchValue]);
};

const useApplySort = (myFleetItems: MyFleetItems) => {
  const sortOrder = useSortOrder();
  return useMemo(() => {
    const shallowCopy = [...myFleetItems];

    if (sortOrder === 'byLicenseAsc' || sortOrder === 'byLicenseDesc') {
      return shallowCopy.sort((a, b) => {
        const firstValue = sortOrder === 'byLicenseAsc' ? (a.autoNumber ?? '') : (b.autoNumber ?? '');
        const secondValue = sortOrder === 'byLicenseAsc' ? (b.autoNumber ?? '') : (a.autoNumber ?? '');
        return firstValue?.localeCompare(secondValue);
      });
    }

    if (sortOrder === 'byLocationStatusAsc' || sortOrder === 'byLocationStatusDesc') {
      return shallowCopy.sort((a, b) => {
        const firstValue = getAutoStatusByDate(
          sortOrder === 'byLocationStatusAsc' ? a.lastActivityDate : b.lastActivityDate
        );
        const secondValue = getAutoStatusByDate(
          sortOrder === 'byLocationStatusAsc' ? b.lastActivityDate : a.lastActivityDate
        );
        return firstValue - secondValue;
      });
    }

    return shallowCopy;
  }, [myFleetItems, sortOrder]);
};

const useFilterByDeviceType = (myFleetItems: MyFleetItems) => {
  const { deviceType } = useGetFilter();
  return useMemo(() => {
    const allowDeviceType = Object.keys(deviceType || {});

    if (allowDeviceType.length === 0) {
      return myFleetItems;
    }

    return myFleetItems.filter(({ deviceType }) => allowDeviceType.includes(deviceType));
  }, [deviceType, myFleetItems]);
};

export const useGetMyFleet = () => {
  const carEBoxLocations = useCarEBoxLocations() ?? EMPTY_ARRAY;
  const userCurrentLocation = useUserCurrentLocation() ?? EMPTY_ARRAY;

  const userCurrentLocationMyFleet = useMemo(
    () =>
      userCurrentLocation.map<MyFleetListItem<UserLocation>>((item) => ({
        autoNumber: item.autoNumber,
        deviceType: 'E100MobileApp',
        lastActivityDate: item.storeTimestampUtc,
        latitude: item.latitude,
        longitude: item.longitude,
        cardNumber: item.cardNumber,
        rawData: item,
      })),
    [userCurrentLocation]
  );

  const eBoxDataMyFleet = useMemo(
    () =>
      carEBoxLocations.map<MyFleetListItem<CarEBoxLocation>>(item => ({
        ...item,
        lastActivityDate: item.timestampUtc,
        deviceType: 'EBox',
        rawData: item
      })),
    [carEBoxLocations]
  );

  const myFleetItems = useMemo(
    () => [...eBoxDataMyFleet, ...userCurrentLocationMyFleet],
    [eBoxDataMyFleet, userCurrentLocationMyFleet]
  );
  const dataAfterSearch = useApplySearchValue(myFleetItems);
  const filterByDeviceType = useFilterByDeviceType(dataAfterSearch);
  const filterData = useApplyFilters(filterByDeviceType);
  const sortData = useApplySort(filterData);

  return sortData;
};
